import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AgentObject } from "@altheia-xyz/types";
import { AppChrome } from "@/components/Chrome";
import { Hallmark, HallmarkBadge } from "@/components/Sigil";
import { fmtAmount, fmtRelTime, PROGRAMS, SOLSCAN_TX } from "@/components/Primitives";
import { useAuth } from "@/components/providers/AuthProvider";
import { api, type AuditEvent, type Chain } from "@/lib/api";
import { ChainBadge } from "@/components/ChainBadge";

interface AuditRow {
  id: string;
  agentId: string;
  agentPda: string; // canonical on-chain identifier — used for the in-app link
  agentName: string;
  chain: Chain;
  tMin: number;
  action: string;
  mint: string;
  amount: number;
  target: string;
  result: "allowed" | "denied";
  reason: string;
  txSignature: string | null;
}

function eventToRow(
  e: AuditEvent & { agent_id: string | null },
  agentName: string,
  agentPda: string,
  chain: Chain,
): AuditRow {
  const ad = e.action_descriptor ?? {};
  const created = new Date(e.created_at).getTime();
  const tMin = Math.max(1, Math.round((Date.now() - created) / 60000));
  return {
    id: e.id,
    agentId: e.agent_id ?? "",
    agentPda,
    agentName,
    chain: e.chain ?? chain,
    tMin,
    action: ad.type ?? e.event_type,
    mint: ad.asset ?? e.asset_mint ?? "",
    amount: ad.amount ?? e.amount_token ?? 0,
    target: ad.target ?? e.destination ?? "",
    result: e.decision === "denied" ? "denied" : "allowed",
    reason: e.reason_detail ?? e.reason_code ?? "",
    txSignature: e.tx_signature,
  };
}

export function ChroniclePage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState<AgentObject[]>([]);
  const [filterAgent, setFilterAgent] = useState<string>("all");
  const [filterResult, setFilterResult] = useState<"all" | "allowed" | "denied">("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    api.listAgents(token).then((r) => setAgents(r.agents)).catch(() => {});
  }, [token]);

  // Refetch events whenever the agent filter or result filter changes.
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const agentNameById = new Map(agents.map((a) => [a.id, a.name]));
    const agentPdaById = new Map(agents.map((a) => [a.id, a.on_chain_pubkey]));
    const agentChainById = new Map<string, Chain>(
      agents.map((a) => [a.id, ((a as unknown as { chain?: Chain }).chain ?? "solana") as Chain]),
    );
    api
      .searchAudit(token, {
        filter: filterResult,
        limit: 100,
        ...(filterAgent !== "all" ? { agent_id: filterAgent } : {}),
      })
      .then((r) => {
        setRows(
          r.events.map((e) =>
            eventToRow(
              e,
              agentNameById.get(e.agent_id ?? "") ?? "—",
              agentPdaById.get(e.agent_id ?? "") ?? "",
              agentChainById.get(e.agent_id ?? "") ?? "solana",
            ),
          ),
        );
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [token, filterAgent, filterResult, agents]);

  // Free-text search filters in-memory across the fetched page.
  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.target.toLowerCase().includes(s) ||
        r.action.toLowerCase().includes(s) ||
        r.reason.toLowerCase().includes(s) ||
        r.agentName.toLowerCase().includes(s),
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    return {
      all: rows.length,
      allowed: rows.filter((r) => r.result === "allowed").length,
      denied: rows.filter((r) => r.result === "denied").length,
    };
  }, [rows]);

  return (
    <AppChrome>
      <div className="page page--chronicle">
        <div className="page__head">
          <div>
            <span className="kicker">All agents · all events</span>
            <h1 className="page__title">Chronicle</h1>
          </div>
          <div className="head-right">
            <a href={SOLSCAN_TX(PROGRAMS.genesisDeployTx)} target="_blank" rel="noopener noreferrer">
              <HallmarkBadge>Merkle root inscribed every 100 blocks ↗</HallmarkBadge>
            </a>
            <button className="btn btn--ghost" disabled title="Coming soon — query the audit DB directly via the API for now.">
              Export CSV
            </button>
          </div>
        </div>

        <div className="chronicle-filters">
          <div className="chronicle-filters__chips">
            <FilterChip
              label="All"
              count={counts.all}
              active={filterResult === "all"}
              onClick={() => setFilterResult("all")}
            />
            <FilterChip
              label="Allowed"
              count={counts.allowed}
              active={filterResult === "allowed"}
              tone="bronze"
              onClick={() => setFilterResult("allowed")}
            />
            <FilterChip
              label="Denied"
              count={counts.denied}
              active={filterResult === "denied"}
              tone="oxblood"
              onClick={() => setFilterResult("denied")}
            />
          </div>
          <div className="chronicle-filters__search">
            <select value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)}>
              <option value="all">All agents ({agents.length})</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search target, reason, action…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="chronicle__table-wrap">
        <table className="chronicle__table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Agent</th>
              <th>Action</th>
              <th>Amount</th>
              <th>Mint</th>
              <th>Target</th>
              <th>Result</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "var(--s-6)", color: "var(--slate-3)" }}>
                  <span className="kicker">Loading audit events…</span>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "var(--s-6)", color: "var(--slate-3)" }}>
                  <span className="kicker">
                    {rows.length === 0 ? "No audit events yet" : "No events match the current filters"}
                  </span>
                </td>
              </tr>
            ) : (
              filtered.slice(0, 50).map((r) => (
                <tr key={r.id} className={r.result === "denied" ? "row--denied" : ""}>
                  <td className="num">{fmtRelTime(r.tMin)}</td>
                  <td>
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      {r.agentId ? (
                        <Link to={`/agents/${r.agentPda || r.agentId}`} className="chronicle__agent-link">
                          {r.agentName}
                        </Link>
                      ) : (
                        <span className="chronicle__agent-link">{r.agentName}</span>
                      )}
                      <ChainBadge chain={r.chain} />
                    </span>
                  </td>
                  <td>{r.action}</td>
                  <td className="num">{r.amount ? fmtAmount(r.amount, r.mint) : "—"}</td>
                  <td className="kicker">{r.mint || "—"}</td>
                  <td>{r.target || "—"}</td>
                  <td>
                    {r.result === "denied" ? (
                      <span className="result--denied">
                        <span className="deny-bar" /> DENIED
                      </span>
                    ) : (
                      <span className="result--allowed">
                        <Hallmark size={11} tone="bronze" /> ALLOWED
                      </span>
                    )}
                  </td>
                  <td className="num chronicle__reason">
                    {r.txSignature && !r.txSignature.startsWith("sdk-") ? (
                      <a
                        href={SOLSCAN_TX(r.txSignature)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chronicle__agent-link"
                      >
                        {r.reason || `${r.txSignature.slice(0, 6)}…`} ↗
                      </a>
                    ) : (
                      r.reason || "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        <div className="chronicle__foot">
          <span>
            {filtered.length === 0
              ? "0 events"
              : search
                ? `Showing ${Math.min(50, filtered.length)} of ${filtered.length} matching · ${rows.length} fetched`
                : `Showing ${Math.min(50, rows.length)} of last ${rows.length} events`}
          </span>
          <span className="kicker">Inscribed on Solana · Merkle anchor commits hourly</span>
        </div>
      </div>

      <ChronicleStyles />
    </AppChrome>
  );
}

function FilterChip({
  label,
  count,
  active,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: "bronze" | "oxblood";
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`filter-chip ${active ? "filter-chip--active" : ""} ${tone === "bronze" ? "filter-chip--bronze" : ""} ${tone === "oxblood" ? "filter-chip--oxblood" : ""}`}
    >
      {label} <span className="num">{count}</span>
    </button>
  );
}

function ChronicleStyles() {
  return (
    <style>{`
      .head-right { display: flex; gap: var(--s-3); align-items: center; }
      .chronicle-filters { display: grid; grid-template-columns: auto 1fr; gap: var(--s-5); align-items: center; padding: var(--s-4) 0; border-bottom: 1px solid var(--line-soft); margin-bottom: var(--s-5); }
      .chronicle-filters__chips { display: flex; gap: var(--s-2); }
      .filter-chip { padding: var(--s-2) var(--s-3); border: 1px solid var(--line); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate); }
      .filter-chip:hover { border-color: var(--bone); color: var(--bone); }
      .filter-chip--active { border-color: var(--bone); color: var(--bone); background: var(--bg-elev); }
      .filter-chip--bronze.filter-chip--active { border-color: var(--bronze); color: var(--bronze); background: var(--bronze-tint); }
      .filter-chip--oxblood.filter-chip--active { border-color: var(--oxblood); color: var(--oxblood); background: var(--oxblood-tint); }
      .chronicle-filters__search { display: flex; gap: var(--s-3); justify-content: flex-end; }
      .chronicle-filters__search select, .chronicle-filters__search input { background: var(--bg); border: 1px solid var(--line); padding: var(--s-2) var(--s-3); font: var(--type-body-small); color: var(--bone); }
      .chronicle-filters__search input { min-width: 280px; }
      .chronicle__table-wrap { overflow-x: auto; }
      .chronicle__table { width: 100%; border-collapse: collapse; min-width: 880px; }
      .chronicle__table thead th { padding: var(--s-3); text-align: left; font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-2); border-bottom: 1px solid var(--line); }
      .chronicle__table tbody td { padding: var(--s-2) var(--s-3); font: var(--type-body-small); border-bottom: 1px solid var(--line-soft); }
      .chronicle__table tbody tr.row--denied { background: var(--oxblood-tint); }
      .chronicle__table tbody tr:hover { background: var(--bg-elev); }
      .chronicle__agent-link { color: var(--bone); border-bottom: 1px solid var(--line-soft); padding-bottom: 1px; }
      .chronicle__agent-link:hover { border-bottom-color: var(--bronze); }
      .chronicle__reason { color: var(--slate-2); }
      .result--allowed, .result--denied { display: inline-flex; align-items: center; gap: var(--s-2); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; }
      .result--allowed { color: var(--bronze); }
      .result--denied { color: var(--oxblood); }
      .deny-bar { width: 10px; height: 2px; background: var(--oxblood); display: inline-block; }
      .chronicle__foot { display: flex; justify-content: space-between; align-items: center; padding: var(--s-4) 0; color: var(--slate); font: var(--type-mono-address); border-top: 1px solid var(--line-soft); margin-top: var(--s-5); }
      @media (max-width: 768px) {
        .chronicle-filters { grid-template-columns: 1fr; }
        .chronicle-filters__search { justify-content: flex-start; flex-direction: column; }
        .chronicle-filters__search input { min-width: 0; width: 100%; }
      }
    `}</style>
  );
}
